import { Box, Grid, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import TitleSubTitle from "../SchemaComponents/TitleSubtitle/TitleSubTitle";

interface SlugFieldProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  // Omit to hide the read-only preview row entirely (e.g. collections, which have
  // no public/rendered URL).
  canonicalUrl?: string;
  layout?: "row" | "column";
}

export const SlugFieldLabel = () => {
  const { t } = useTranslation();
  return (
    <TitleSubTitle
      title={`${t("slug_field_label")} *`}
      subTitle={t("slug_field_description")}
      titleVariant='h6medium'
      subTitleVariant='p4regular'
      titleId='slug_label'
    />
  );
};

export const SlugFieldInput = ({
  value,
  onChange,
  onBlur,
  disabled = false,
  canonicalUrl,
}: Omit<SlugFieldProps, "layout">) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ width: "100%" }}>
      <TextField
        variant='filled'
        size='small'
        fullWidth
        name='slug'
        id='slug'
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={t("slug_placeholder")}
        inputProps={{ "aria-labelledby": "slug_label" }}
      />
      {canonicalUrl && (
        <Box sx={{ marginTop: "8px" }}>
          <Typography variant='p4regular'>{t("canonical_url_preview_label")}</Typography>
          <Typography
            variant='p4regular'
            sx={{ wordBreak: "break-all" }}
            data-testid='canonical-url-preview'>
            {canonicalUrl}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const SlugField = ({
  value,
  onChange,
  onBlur,
  disabled = false,
  canonicalUrl,
  layout = "row",
}: SlugFieldProps) => {
  if (layout === "column") {
    return (
      <Box id='slug_id'>
        <SlugFieldLabel />
        <Box sx={{ marginTop: "8px" }}>
          <SlugFieldInput
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            canonicalUrl={canonicalUrl}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box id='slug_id'>
      <Grid container rowSpacing={1}>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={5}
          sx={{
            paddingRight: {
              xs: 0,
              sm: 0,
              md: 0,
              lg: "55px",
            },
          }}>
          <SlugFieldLabel />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={7}>
          <SlugFieldInput
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            canonicalUrl={canonicalUrl}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SlugField;
